import { getCodeBySystem, MedplumClient } from "@medplum/core";
import { Patient } from "@medplum/fhirtypes";

import filter from "lodash/filter";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";
import uniq from "lodash/uniq";
import { v4 as uuidv4 } from "uuid";

import { Systems } from "@/fhir/enums/systems";
import { isMedplumIfMatchConflictError } from "@/utils/medplum-if-match-conflict";

// Types
interface CollisionResult {
  hasCollisions: boolean;
  collisions: string[];
}

interface MrnValidationResult {
  isValid: boolean;
  originalDigits?: string;
  checkDigit?: number;
  error?: string;
}

interface BatchProcessingResult {
  newMrns: string[];
  collisionRate: number;
  candidatesGenerated: number;
  duplicatesSkipped: number;
}

interface BatchProcessingConfig {
  batchSize: number;
  collisionThreshold: number;
  maxAttempts: number;
  collisionBuffer: number;
}

/**
 * Configuration constants for MRN generation
 */
export const MrnConfig = {
  FORMAT_REGEX: /^\d{4}-\d{4}$/,
  LENGTH: 8,
  BASE_DIGITS_LENGTH: 7,
  UUID_HEX_LENGTH: 7,
  MAX_COLLISION_ATTEMPTS: 10,
  OWNERSHIP_SEARCH_ATTEMPTS: 5,
  OWNERSHIP_SEARCH_DELAY_MS: 75,
  OWNERSHIP_CONFIRMATION_DELAY_MS: 150,
  /** Per-identifier search page size; only need enough to detect duplicate ownership (0/1/2+). */
  OWNERSHIP_SEARCH_COUNT: 2,
  BATCH_PROCESSING: {
    SIZE: 50,
    COLLISION_THRESHOLD: 0.1,
    COLLISION_BUFFER: 1.5,
    MAX_CANDIDATE_ATTEMPTS: 1000,
  },
} as const;

/**
 * Luhn algorithm utilities
 */
export const LuhnUtils = {
  /**
   * Calculate Luhn sum for a given number
   */
  calculateSum(number: string, includeCheckDigit: boolean): number {
    let sum = 0;
    let isEven = !includeCheckDigit;

    for (let i = number.length - 1; i >= 0; i--) {
      let digit = parseInt(number.charAt(i));

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum;
  },

  /**
   * Generate Luhn check digit for a number
   */
  generateCheckDigit(number: string): number {
    const sum = LuhnUtils.calculateSum(number, false);
    return (10 - (sum % 10)) % 10;
  },

  /**
   * Validate a complete number using Luhn algorithm
   */
  validate(digits: string): boolean {
    const sum = LuhnUtils.calculateSum(digits, true);
    return sum % 10 === 0;
  },

  /**
   * Add Luhn check digit to a number string
   */
  addCheckDigit(number: string): string {
    const checkDigit = LuhnUtils.generateCheckDigit(number);
    return number + checkDigit.toString();
  },

  /**
   * Validate and extract the base number from a Luhn-validated string
   */
  validateAndExtract(numberWithCheckDigit: string): {
    isValid: boolean;
    baseNumber?: string;
    checkDigit?: number;
  } {
    if (!numberWithCheckDigit || numberWithCheckDigit.length < 2) {
      return { isValid: false };
    }

    const isValid = LuhnUtils.validate(numberWithCheckDigit);
    if (!isValid) {
      return { isValid: false };
    }

    const baseNumber = numberWithCheckDigit.slice(0, -1);
    const checkDigit = parseInt(numberWithCheckDigit.slice(-1));

    return {
      isValid: true,
      baseNumber,
      checkDigit,
    };
  },
};

/**
 * UUID to numeric conversion utilities
 */
export const UuidConverter = {
  /**
   * Convert UUID to 7-digit numeric string
   */
  toNumeric(uuid: string): string {
    const hexString = uuid.replace(/-/g, "").substring(0, MrnConfig.UUID_HEX_LENGTH);
    const decimal = parseInt(hexString, 16);

    return decimal
      .toString()
      .padStart(MrnConfig.BASE_DIGITS_LENGTH, "0")
      .slice(-MrnConfig.BASE_DIGITS_LENGTH);
  },
};

/**
 * MRN formatting utilities
 */
export const MrnFormatter = {
  /**
   * Format a number as MRN with dash
   */
  format(number: string): string {
    return `${number.slice(0, 4)}-${number.slice(4)}`;
  },

  /**
   * Remove formatting from MRN
   */
  unformat(mrn: string): string {
    return mrn.replace("-", "");
  },

  /**
   * Validate MRN format
   */
  isValidFormat(mrn: string): boolean {
    return MrnConfig.FORMAT_REGEX.test(mrn);
  },
};

/**
 * MRN identifier creation utility
 */
export const MrnIdentifierFactory = {
  /**
   * Create a standard MRN identifier
   */
  create(mrn: string) {
    return {
      system: Systems.QH,
      value: mrn,
      use: "official" as const,
      type: {
        coding: [
          {
            system: Systems.IdentifierType,
            code: "MR",
            display: "Medical Record Number",
          },
        ],
      },
    };
  },
};

/**
 * Generate a new MRN from UUID
 */
export function generateMrnFromUuid(uuid: string): string {
  const sevenDigits = UuidConverter.toNumeric(uuid);
  const checkDigit = LuhnUtils.generateCheckDigit(sevenDigits);
  const fullNumber = sevenDigits + checkDigit;

  return MrnFormatter.format(fullNumber);
}

/**
 * Generate a new MRN
 */
export function generateMrn(): string {
  const uuid = uuidv4();
  return generateMrnFromUuid(uuid);
}

/**
 * Check if MRN already exists in Medplum
 */
export async function checkMrnExists(medplumClient: MedplumClient, mrn: string): Promise<boolean> {
  const patients = await findPatientsByMrn(medplumClient, mrn);
  return patients.length > 0;
}

/**
 * Find all Patient resources that carry the given MRN identifier.
 * Search is scoped to one MRN value (not total patient count); two results is enough to detect a collision.
 */
export async function findPatientsByMrn(
  medplumClient: MedplumClient,
  mrn: string,
): Promise<Patient[]> {
  try {
    const searchResult = await medplumClient.search("Patient", {
      identifier: `${Systems.QH}|${mrn}`,
      _count: String(MrnConfig.OWNERSHIP_SEARCH_COUNT),
    });

    const entries = searchResult.entry ?? [];
    if (isEmpty(entries)) {
      return [];
    }

    return entries
      .map((entry) => entry.resource as Patient)
      .filter((patient) => patient?.resourceType === "Patient");
  } catch (error) {
    throw new Error(`Failed to search patients by MRN: ${error}`);
  }
}

function delayMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Decide MRN ownership from a bounded search result.
 * @returns `false` when another patient shares the MRN (2+ owners, or a single non-matching owner),
 * `true` when only the given patient owns it, and `null` when the search found nothing so the answer
 * is not yet known.
 */
export function ownershipVerdict(patients: Patient[], patientId: string): boolean | null {
  if (patients.length > 1) {
    return false;
  }
  if (patients.length === 1) {
    return patients[0].id === patientId;
  }
  return null;
}

/**
 * Best-effort post-write ownership check. The identifier search index is eventually consistent, so an
 * empty result can mean "no collision" or "the colliding patient is not indexed yet". This retries
 * empty results with growing delays, then falls back to reading the patient directly and searching
 * once more. A `true` result is a best guess under index lag, not a hard uniqueness guarantee.
 * @returns `true` when the patient appears to own the MRN, `false` when another owner is found.
 */
export async function confirmMrnOwnershipAfterWrite(
  medplumClient: MedplumClient,
  patientId: string,
  mrn: string,
): Promise<boolean> {
  const searchAttempts = MrnConfig.OWNERSHIP_SEARCH_ATTEMPTS;

  for (let attempt = 0; attempt < searchAttempts; attempt++) {
    const verdict = ownershipVerdict(await findPatientsByMrn(medplumClient, mrn), patientId);
    if (verdict !== null) {
      return verdict;
    }

    if (attempt < searchAttempts - 1) {
      await delayMs(MrnConfig.OWNERSHIP_SEARCH_DELAY_MS * (attempt + 1));
    }
  }

  try {
    const currentPatient = await medplumClient.readResource("Patient", patientId);
    if (PatientMrnUtils.getMrn(currentPatient) !== mrn) {
      return false;
    }

    await delayMs(MrnConfig.OWNERSHIP_CONFIRMATION_DELAY_MS);
    const confirmationPatients = await findPatientsByMrn(medplumClient, mrn);
    return ownershipVerdict(confirmationPatients, patientId) ?? true;
  } catch (error) {
    console.warn(`MRN ownership read failed for Patient/${patientId}`, { mrn, error });
    throw error;
  }
}

/**
 * Persists MRN identifier changes with optimistic concurrency when versionId is available.
 */
async function persistPatientMrn(
  medplumClient: MedplumClient,
  patient: Patient,
  mrn: string,
): Promise<Patient & { id: string }> {
  const updatedPatient = PatientMrnUtils.updateMrn(patient, mrn);
  const versionId = patient.meta?.versionId;

  let savedPatient: Patient;
  if (!patient.id || !versionId) {
    savedPatient = await medplumClient.updateResource(updatedPatient);
  } else {
    const hasExistingIdentifiers = (patient.identifier?.length ?? 0) > 0;
    const identifierPatchOp = hasExistingIdentifiers ? "replace" : "add";

    savedPatient = await medplumClient.patchResource("Patient", patient.id, [
      { op: "test", path: "/meta/versionId", value: versionId },
      { op: identifierPatchOp, path: "/identifier", value: updatedPatient.identifier ?? [] },
    ]);
  }

  if (!savedPatient.id) {
    throw new Error("Persisted Patient is missing an id after MRN write");
  }

  return savedPatient as Patient & { id: string };
}

/**
 * Persist an MRN, tolerating a single stale-version (if-match) conflict by re-reading the patient and
 * retrying once. Other errors propagate unchanged.
 */
async function persistPatientMrnWithReread(
  medplumClient: MedplumClient,
  patient: Patient,
  mrn: string,
): Promise<Patient & { id: string }> {
  try {
    return await persistPatientMrn(medplumClient, patient, mrn);
  } catch (error) {
    if (!isMedplumIfMatchConflictError(error) || !patient.id) {
      throw error;
    }

    const fresh = await medplumClient.readResource("Patient", patient.id);
    return persistPatientMrn(medplumClient, fresh, mrn);
  }
}

function logMrnCollisionEvent(
  event: "detected" | "remediated",
  patientId: string,
  details: { attempt?: number; maxAttempts?: number; forceReassign?: boolean },
): void {
  console.warn(`MRN collision ${event} for Patient/${patientId}`, {
    attempt: details.attempt,
    maxAttempts: details.maxAttempts,
    forceReassign: details.forceReassign,
  });
}

/**
 * Assign a freshly generated unique MRN to a patient, retrying when a concurrent create wins the same
 * value. `forceReassign` only affects remediation logging; the decision to reassign is owned by
 * {@link ensurePatientMrn}.
 */
async function assignNewUniqueMrn(
  medplumClient: MedplumClient,
  patient: Patient,
  options?: { forceReassign?: boolean },
): Promise<Patient> {
  if (!patient.id) {
    throw new Error("Patient id is required to assign a unique MRN");
  }

  const maxAttempts = MrnConfig.MAX_COLLISION_ATTEMPTS;
  let currentPatient = patient;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const mrn = await generateUniqueMrn(medplumClient);
    let savedPatient: Patient & { id: string };

    try {
      savedPatient = await persistPatientMrnWithReread(medplumClient, currentPatient, mrn);
    } catch (error) {
      if (!isMedplumIfMatchConflictError(error)) {
        throw error;
      }

      currentPatient = await medplumClient.readResource("Patient", patient.id);
      logMrnCollisionEvent("detected", patient.id, { attempt, maxAttempts });
      continue;
    }

    currentPatient = savedPatient;

    if (await confirmMrnOwnershipAfterWrite(medplumClient, savedPatient.id, mrn)) {
      if (attempt > 0 || options?.forceReassign) {
        logMrnCollisionEvent("remediated", patient.id, {
          attempt,
          maxAttempts,
          forceReassign: options?.forceReassign,
        });
      }
      return savedPatient;
    }

    logMrnCollisionEvent("detected", patient.id, { attempt, maxAttempts });
  }

  throw new Error(
    `Failed to assign a unique MRN to Patient/${patient.id} after ${maxAttempts} attempts`,
  );
}

/**
 * Ensure a patient has a unique MRN. Assigns one when missing. When `remediateCollisions` is true
 * (write paths: create/update bots), it also confirms post-write ownership and reassigns on a
 * detected collision. Read paths (client chart load) pass `remediateCollisions: false` to backfill a
 * missing MRN only, avoiding remediation search/write latency on every load.
 */
export async function ensurePatientMrn(
  medplumClient: MedplumClient,
  patient: Patient,
  { remediateCollisions = true }: { remediateCollisions?: boolean } = {},
): Promise<Patient> {
  if (!patient.id) {
    throw new Error("Patient id is required to ensure a unique MRN");
  }

  const mrn = PatientMrnUtils.getMrn(patient);
  if (!mrn) {
    return assignNewUniqueMrn(medplumClient, patient);
  }

  if (!remediateCollisions) {
    return patient;
  }

  if (await confirmMrnOwnershipAfterWrite(medplumClient, patient.id, mrn)) {
    return patient;
  }

  logMrnCollisionEvent("detected", patient.id, { forceReassign: true });
  return assignNewUniqueMrn(medplumClient, patient, { forceReassign: true });
}

/**
 * Validate MRN format and Luhn check digit
 */
export function validateMrn(mrn: string): MrnValidationResult {
  if (!MrnFormatter.isValidFormat(mrn)) {
    return {
      isValid: false,
      error: "Invalid MRN format. Expected XXXX-XXXX",
    };
  }

  const digits = MrnFormatter.unformat(mrn);

  if (digits.length !== MrnConfig.LENGTH) {
    return {
      isValid: false,
      error: `Invalid MRN length. Expected ${MrnConfig.LENGTH} digits, got ${digits.length}`,
    };
  }

  const isValid = LuhnUtils.validate(digits);

  if (!isValid) {
    return {
      isValid: false,
      error: "Invalid Luhn check digit",
    };
  }

  return {
    isValid: true,
    originalDigits: digits.slice(0, MrnConfig.BASE_DIGITS_LENGTH),
    checkDigit: parseInt(digits.charAt(MrnConfig.BASE_DIGITS_LENGTH)),
  };
}

/**
 * Extract the original 7 digits from MRN (without check digit)
 */
export function extractOriginalDigits(mrn: string): string | null {
  const validation = validateMrn(mrn);
  return validation.isValid ? validation.originalDigits || null : null;
}

/**
 * Get check digit from MRN
 */
export function getCheckDigit(mrn: string): number | null {
  const validation = validateMrn(mrn);
  return validation.isValid && validation.checkDigit !== undefined ? validation.checkDigit : null;
}

/**
 * Generate multiple MRNs (without collision checking)
 */
export function generateMultipleMrns(count: number): string[] {
  if (count < 0) {
    throw new Error("Count must be non-negative");
  }

  const mrns: string[] = [];
  for (let i = 0; i < count; i++) {
    mrns.push(generateMrn());
  }

  return mrns;
}

/**
 * Check for collisions in a set of MRNs
 */
export function checkForCollisions(mrns: string[]): CollisionResult {
  const seen = new Set<string>();
  const collisions: string[] = [];

  for (const mrn of mrns) {
    if (seen.has(mrn)) {
      collisions.push(mrn);
    } else {
      seen.add(mrn);
    }
  }

  return {
    hasCollisions: !isEmpty(collisions),
    collisions,
  };
}

/**
 * Get MRN statistics
 */
export function getMrnStats(mrns: string[]): {
  total: number;
  valid: number;
  invalid: number;
  collisions: number;
  unique: number;
} {
  const valid = size(filter(mrns, (mrn) => validateMrn(mrn).isValid));
  const collisionResult = checkForCollisions(mrns);
  const unique = size(uniq(mrns));

  return {
    total: size(mrns),
    valid,
    invalid: size(mrns) - valid,
    collisions: size(collisionResult.collisions),
    unique,
  };
}

/**
 * Batch processing utilities
 */
export const BatchProcessor = {
  /**
   * Calculate maximum attempts based on target count with safety limits
   */
  calculateMaxAttempts(
    targetCount: number,
    maxAttempts: number = MrnConfig.MAX_COLLISION_ATTEMPTS,
  ): number {
    const baseAttempts = Math.max(maxAttempts, targetCount * 3);
    const maxSafeAttempts = 100;
    return Math.min(baseAttempts, maxSafeAttempts);
  },

  /**
   * Calculate optimal batch size for current needs
   */
  calculateBatchSize(
    remainingCount: number,
    batchSize: number = MrnConfig.BATCH_PROCESSING.SIZE,
    collisionBuffer: number = MrnConfig.BATCH_PROCESSING.COLLISION_BUFFER,
  ): number {
    return Math.min(batchSize, Math.ceil(remainingCount * collisionBuffer));
  },

  /**
   * Generate unique candidate MRNs with safety limits
   */
  generateUniqueCandidates(
    batchSize: number,
    existingMrns: Set<string>,
  ): { candidates: string[]; duplicatesSkipped: number } {
    const candidates: string[] = [];
    const candidateSet = new Set<string>();
    let duplicatesSkipped = 0;
    let attempts = 0;
    const maxAttempts = MrnConfig.BATCH_PROCESSING.MAX_CANDIDATE_ATTEMPTS;

    while (candidates.length < batchSize && attempts < maxAttempts) {
      const candidate = generateMrn();
      const isDuplicate = candidateSet.has(candidate) || existingMrns.has(candidate);

      if (isDuplicate) {
        duplicatesSkipped++;
      } else {
        candidates.push(candidate);
        candidateSet.add(candidate);
      }

      attempts++;
    }

    if (attempts >= maxAttempts && candidates.length < batchSize) {
      console.warn(
        `Candidate generation hit safety limit after ${maxAttempts} attempts. ` +
          `Generated ${candidates.length}/${batchSize} candidates. ` +
          `Skipped ${duplicatesSkipped} duplicates.`,
      );
    }

    return { candidates, duplicatesSkipped };
  },

  /**
   * Check multiple MRNs for existence in a single batch operation
   */
  async batchCheckMrnExists(medplumClient: MedplumClient, mrns: string[]): Promise<Set<string>> {
    if (mrns.length === 0) {
      return new Set();
    }

    try {
      const identifierQueries = mrns.map((mrn) => `${Systems.QH}|${mrn}`);
      const searchResult = await medplumClient.search("Patient", {
        identifier: identifierQueries.join(","),
        _count: "1000",
      });

      const existingMrns = new Set<string>();
      if (searchResult.entry) {
        for (const entry of searchResult.entry) {
          const patient = entry.resource as unknown as Patient;
          if (patient?.identifier) {
            for (const identifier of patient.identifier) {
              if (identifier.system === Systems.QH && identifier.value) {
                existingMrns.add(identifier.value);
              }
            }
          }
        }
      }

      return existingMrns;
    } catch (error) {
      throw new Error(`Failed to batch check MRN existence: ${error}`);
    }
  },

  /**
   * Filter out candidates that collide with existing MRNs
   */
  filterNonCollidingCandidates(
    candidates: string[],
    existingInMedplum: Set<string>,
    maxCount: number,
  ): string[] {
    const nonColliding: string[] = [];

    for (const candidate of candidates) {
      if (!existingInMedplum.has(candidate) && nonColliding.length < maxCount) {
        nonColliding.push(candidate);
      }
    }

    return nonColliding;
  },

  /**
   * Process a single batch of MRN generation
   */
  async processBatch(
    medplumClient: MedplumClient,
    targetCount: number,
    currentMrns: string[],
    existingMrns: Set<string>,
    attemptNumber: number,
    config: BatchProcessingConfig,
  ): Promise<BatchProcessingResult> {
    const remainingCount = targetCount - currentMrns.length;
    const batchSize = BatchProcessor.calculateBatchSize(
      remainingCount,
      config.batchSize,
      config.collisionBuffer,
    );

    const candidateResult = BatchProcessor.generateUniqueCandidates(batchSize, existingMrns);
    const existingInMedplum = await BatchProcessor.batchCheckMrnExists(
      medplumClient,
      candidateResult.candidates,
    );
    const newMrns = BatchProcessor.filterNonCollidingCandidates(
      candidateResult.candidates,
      existingInMedplum,
      remainingCount,
    );

    newMrns.forEach((mrn) => existingMrns.add(mrn));

    return {
      newMrns,
      collisionRate:
        candidateResult.candidates.length > 0
          ? existingInMedplum.size / candidateResult.candidates.length
          : 0,
      candidatesGenerated: candidateResult.candidates.length,
      duplicatesSkipped: candidateResult.duplicatesSkipped,
    };
  },

  /**
   * Log warning for high collision rates
   */
  logHighCollisionRate(
    collisionRate: number,
    generatedCount: number,
    targetCount: number,
    attempts: number,
  ): void {
    console.warn(
      `High MRN collision rate detected: ${(collisionRate * 100).toFixed(1)}%. ` +
        `Generated ${generatedCount}/${targetCount} MRNs in ${attempts} attempts. ` +
        `Consider increasing batch size or checking system capacity.`,
    );
  },

  /**
   * Validate that batch generation completed successfully
   */
  validateBatchCompletion(uniqueMrns: string[], targetCount: number, attempts: number): void {
    if (uniqueMrns.length < targetCount) {
      throw new Error(
        `Failed to generate ${targetCount} unique MRNs after ${attempts} attempts. ` +
          `Only generated ${uniqueMrns.length}. This may indicate system saturation or ` +
          `insufficient MRN space remaining.`,
      );
    }
  },
};

/**
 * Generate a unique MRN that doesn't exist in Medplum
 */
export async function generateUniqueMrn(medplumClient: MedplumClient): Promise<string> {
  const result = await generateMultipleUniqueMrns(medplumClient, 1);
  return result[0];
}

/**
 * Generate multiple unique MRNs using optimized batch processing
 */
export async function generateMultipleUniqueMrns(
  medplumClient: MedplumClient,
  count: number,
  config?: Partial<BatchProcessingConfig>,
): Promise<string[]> {
  if (count < 0) {
    throw new Error("Count must be non-negative");
  }

  if (count === 0) {
    return [];
  }

  const fullConfig: BatchProcessingConfig = {
    batchSize: MrnConfig.BATCH_PROCESSING.SIZE,
    collisionThreshold: MrnConfig.BATCH_PROCESSING.COLLISION_THRESHOLD,
    maxAttempts: MrnConfig.MAX_COLLISION_ATTEMPTS,
    collisionBuffer: MrnConfig.BATCH_PROCESSING.COLLISION_BUFFER,
    ...config,
  };

  const uniqueMrns: string[] = [];
  const existingMrns = new Set<string>();
  let attempts = 0;
  const maxAttempts = BatchProcessor.calculateMaxAttempts(count, fullConfig.maxAttempts);

  while (uniqueMrns.length < count && attempts < maxAttempts) {
    const batchResult = await BatchProcessor.processBatch(
      medplumClient,
      count,
      uniqueMrns,
      existingMrns,
      attempts,
      fullConfig,
    );

    uniqueMrns.push(...batchResult.newMrns);

    if (batchResult.newMrns.length === 0 && attempts > 3) {
      throw new Error(
        `MRN generation stagnated after ${attempts} attempts. ` +
          `Generated ${uniqueMrns.length}/${count} MRNs. This may indicate system saturation.`,
      );
    }

    if (batchResult.collisionRate > fullConfig.collisionThreshold && attempts > 1) {
      BatchProcessor.logHighCollisionRate(
        batchResult.collisionRate,
        uniqueMrns.length,
        count,
        attempts,
      );
    }

    attempts++;
  }

  BatchProcessor.validateBatchCompletion(uniqueMrns, count, attempts);
  return uniqueMrns;
}

/**
 * Patient MRN management utilities
 */
export const PatientMrnUtils = {
  /**
   * Add MRN to an existing Patient resource
   */
  addMrn(patient: Patient, mrn: string): Patient {
    const mrnIdentifier = MrnIdentifierFactory.create(mrn);

    return {
      ...patient,
      identifier: [...(patient.identifier || []), mrnIdentifier],
    };
  },

  /**
   * Update existing MRN on Patient resource
   */
  updateMrn(patient: Patient, mrn: string): Patient {
    const mrnIdentifier = MrnIdentifierFactory.create(mrn);

    const otherIdentifiers = filter(
      patient.identifier || [],
      (id) => getCodeBySystem(id.type || {}, Systems.IdentifierType) !== "MR",
    );

    return {
      ...patient,
      identifier: [...otherIdentifiers, mrnIdentifier],
    };
  },

  /**
   * Get MRN from Patient resource
   */
  getMrn(patient: Patient): string | null {
    const mrnIdentifier = find(
      patient.identifier || [],
      (id) => getCodeBySystem(id.type || {}, Systems.IdentifierType) === "MR",
    );
    return isNil(mrnIdentifier?.value) ? null : mrnIdentifier.value;
  },

  /**
   * Create a Patient with a unique MRN
   */
  async createWithUniqueMrn(
    medplumClient: MedplumClient,
    patientData: Partial<Patient>,
  ): Promise<Patient> {
    const uniqueMrn = await generateUniqueMrn(medplumClient);
    return PatientMrnUtils.addMrn(patientData as Patient, uniqueMrn);
  },

  /**
   * Create multiple Patients with unique MRNs
   */
  async createMultipleWithUniqueMrns(
    medplumClient: MedplumClient,
    patientsData: Partial<Patient>[],
  ): Promise<Patient[]> {
    if (patientsData.length === 0) {
      return [];
    }

    const uniqueMrns = await generateMultipleUniqueMrns(medplumClient, patientsData.length);

    return patientsData.map((patientData, index) =>
      PatientMrnUtils.addMrn(patientData as Patient, uniqueMrns[index]),
    );
  },
};

/**
 * Update a patient while preserving existing MRN
 * This function ensures that MRNs are never accidentally overwritten
 */
export function updatePatientPreservingMrn(patient: Patient, updates: Partial<Patient>): Patient {
  const existingMrn = PatientMrnUtils.getMrn(patient);

  const updatedPatient = {
    ...patient,
    ...updates,
  };

  // If we had an MRN before, ensure it's preserved
  if (existingMrn && !PatientMrnUtils.getMrn(updatedPatient)) {
    return PatientMrnUtils.addMrn(updatedPatient, existingMrn);
  }

  return updatedPatient;
}

// Export backward-compatible standalone functions for existing code
export const addMrnToPatient = PatientMrnUtils.addMrn;
export const updatePatientMrn = PatientMrnUtils.updateMrn;
export const getPatientMrn = PatientMrnUtils.getMrn;
export const createPatientWithUniqueMrn = PatientMrnUtils.createWithUniqueMrn;
